import React from "react";
import _ from "lodash";
import { Formik } from "formik";
import {
  MdKeyboardBackspace,
  MdDelete,
  MdAssignmentInd,
  MdFileDownload,
  MdDone,
  MdInsertPhoto,
} from "react-icons/md";
import { MdAdd } from "react-icons/md";
import Spinner from "~/components/spinner";
import { TextArea } from "~/components/form";
import DataTable from "~/components/datatable";
import confirm from "~/components/confirm";
import { IconButton } from "~/components/crud/styles";
import useEventMatchday from "~/view-data/use-event-matchday";
import useEvents from "~/view-data/use-events";
import Details from "~/views/events/matchday/details";
import FloatingButton from "~/components/floatbutton";
import {
  Container,
  TableContainer,
  ExpandContainer,
} from "~/views/events/matchday/styles";
import EmployeeModal from "~/views/events/matchday/modal-employee";
import CredentialModal from "~/views/events/accredit-approval/modal-credential";
import { formats } from "~/helper";
import { ProtocolCOVIDModal } from "~/views/match-day/covid";

function EventMatchday({ history }) {
  const { state, actions } = useEventMatchday(),
    { actions: eventActions } = useEvents();

  const columns = [
      {
        name: "CNPJ",
        selector: "identify",
        width: "150px",
        format: (row) => formats.cnpj(_.get(row, "identify")),
      },
      {
        name: "Fornecedor",
        selector: "name",
        format: (row) => _.get(row, "name"),
      },
      {
        name: "Valor",
        selector: "employeesValue",
        right: true,
        width: "110px",
        format: (row) => formats.currency(_.get(row, "employeesValue")),
      },
      {
        name: "Colaboradores",
        selector: "employees",
        right: true,
        width: "110px",
        format: (row) => _.size(_.get(row, "employees")),
      },
      {
        name: "Check-ins",
        selector: "employeesIn",
        right: true,
        width: "95px",
        format: (row) => _.get(row, "employeesIn"),
      },
      {
        name: "Check-outs",
        selector: "employeesOut",
        right: true,
        width: "95px",
        format: (row) => _.get(row, "employeesOut"),
      },
      {
        name: "P-COVID",
        selector: "employeesPCovid",
        right: true,
        width: "95px",
        format: (row) => _.get(row, "employeesPCovid"),
      },
    ],
    personColumns = [
      {
        name: "Colaborador",
        selector: "person",
        wrap: true,
        format: (row) => _.get(row, "person.name"),
      },
      {
        name: "Função",
        selector: "person",
        width: "180px",
        wrap: true,
        format: (row) => _.get(row, "person.role.name"),
      },
      {
        name: "Valor",
        selector: "person",
        right: true,
        width: "120px",
        format: (row) => formats.currency(_.get(row, "person.role.value") || 0),
      },
      {
        name: "Credencial",
        selector: "credentialNumber",
        width: "120px",
        center: true,
        format: (row) => _.get(row, "credentialNumber"),
      },
      {
        name: "Check-in",
        selector: "checkInDate",
        width: "105px",
        format: (row) =>
          formats.dateTimeZone(_.get(row, "checkInDate"), "dd/MM HH:mm"),
      },
      {
        name: "Check-out",
        selector: "checkOutDate",
        width: "105px",
        format: (row) =>
          formats.dateTimeZone(_.get(row, "checkOutDate"), "dd/MM HH:mm"),
      },
    ];

  const handleRemoveEmployee = async (row) => {
    const result = await confirm.show({
      title: "Atenção",
      text: `Deseja realmente remover o colaborador '${_.get(
        row,
        "person.name"
      )}' do evento?`,
    });

    if (result) {
      actions.removeEmployee(row);
    }
  };

  const handleCreateCredentials = (mode, emails) => {
    actions.createCredentials(_.get(state, "modalData.supplier"), mode, emails);
  };

  if (state.mode === "running") {
    columns.push({
      name: "Ações",
      selector: "name",
      center: true,
      width: "75px",
      cell: (row) => (
        <IconButton size={26} onClick={() => actions.openCredentialModal(row)}>
          <MdAssignmentInd />
        </IconButton>
      ),
    });
  }

  if (state.mode === "running") {
    personColumns.push({
      name: "Ações",
      selector: "name",
      center: true,
      width: "75px",
      cell: (row) => {
        const vaccinationCertificate = _.get(
          row,
          "person.vaccinationCertificate"
        );
        const checkUpPCR = _.get(row, "person.checkUpPCR");
        const hasPCovid = () => vaccinationCertificate || checkUpPCR;
        return (
          <>
            <IconButton size={26} onClick={() => handleRemoveEmployee(row)}>
              <MdDelete />
            </IconButton>
            <IconButton
              size={26}
              onClick={() => actions.openCovidModal(row)}
              disabled={!hasPCovid()}
              color={hasPCovid() ? "green" : "red"}
            >
              <MdInsertPhoto />
            </IconButton>
          </>
        );
      },
    });
  }

  const PersonRow = ({ data }) => {
    return (
      <ExpandContainer>
        <DataTable
          columns={personColumns}
          data={{ rows: _.get(data, "employees") || [] }}
          noPagination={true}
          keyField="id"
          emptyText="Nenhum colaborador vinculado"
          extraOptions={{
            ignoreRowClicked: true,
            highlightOnHover: false,
            selectableRows: false,
            selectableRowsHighlight: false,
          }}
        />
      </ExpandContainer>
    );
  };

  const handleExportPdf = async () => {
    const result = await confirm.show({
      title: "Atenção",
      text: `Deseja realmente exportar os dados do evento?`,
    });

    if (result) {
      actions.exportPdf();
    }
  };

  const handleFinish = async (values) => {
    const result = await confirm.show({
      title: "Atenção",
      text: `Deseja realmente finalizar o evento? essa ação não pode ser desfeita!`,
    });

    if (result) {
      actions.finish(values);
    }
  };

  return (
    <>
      {state.loading ? (
        <Spinner visible={true} />
      ) : (
        <Formik
          enableReinitialize={true}
          validateOnMount={true}
          initialValues={{
            observation: _.get(state, "data.event.observation") || "",
          }}
        >
          {({ values, errors, touched, isValid }) => (
            <Container>
              <div className="contents">
                <Details
                  model={state.data}
                  goToEvents={actions.goToEvents}
                  changeStatus={eventActions.changeStatus}
                />

                <TableContainer>
                  <div className="list">
                    <DataTable
                      columns={columns}
                      data={{ rows: _.get(state.data, "suppliers") || [] }}
                      noPagination={true}
                      keyField="id"
                      emptyText="Nenhum fornecedor vinculado"
                      extraOptions={{
                        ignoreRowClicked: true,
                        expandableRows: true,
                        highlightOnHover: true,
                        expandOnRowClicked: true,
                        selectableRows: false,
                        selectableRowsHighlight: false,
                        expandableRowExpanded: actions.isExpanded,
                        onRowExpandToggled: actions.setExpanded,
                        expandableRowsComponent: <PersonRow />,
                      }}
                    />
                    {state.mode === "running" && (
                      <FloatingButton
                        icon={MdAdd}
                        size={44}
                        right={15}
                        bottom={140}
                        onClick={actions.openEmployeeModal}
                      />
                    )}
                  </div>

                  <div className="form">
                    <TextArea
                      name="observation"
                      label="Sumário"
                      rows={3}
                      disabled={state.mode !== "running"}
                    />
                  </div>
                </TableContainer>
              </div>

              <div className="toolbar">
                <IconButton
                  title="Cancelar"
                  onClick={() => history.push("/events")}
                >
                  <MdKeyboardBackspace />
                </IconButton>

                {state.mode === "finished" && (
                  <IconButton title="Download" onClick={handleExportPdf}>
                    <MdFileDownload />
                  </IconButton>
                )}
                {state.mode === "running" && (
                  <IconButton
                    title="Finalizar o evento"
                    onClick={() => handleFinish(values)}
                  >
                    <MdDone />
                  </IconButton>
                )}
              </div>
            </Container>
          )}
        </Formik>
      )}

      <CredentialModal
        loading={state.modalLoading}
        data={state.modalData || {}}
        handleOnSubmit={handleCreateCredentials}
        isOpen={state.showCredentialModal}
        closeModal={actions.closeCredentialModal}
        title={<>Gerar credenciais</>}
      />

      <EmployeeModal
        loading={state.modalLoading}
        data={state.modalData || {}}
        handleOnSubmit={actions.saveEmployeeModal}
        isOpen={state.showEmployeeModal}
        onListRoles={actions.listRoles}
        onListPersons={actions.listPersons}
        onListSuppliers={actions.listSuppliers}
        closeModal={actions.closeEmployeeModal}
        title={<>Novo Colaborador</>}
      />

      <ProtocolCOVIDModal
        data={state.modalPCovidData}
        isOpen={state.showCovidModal}
        closeModal={actions.closeCovidModal}
        title={<>Protocolo COVID</>}
        width="60%"
      />
    </>
  );
}

export default EventMatchday;
